/* ------------------------------------------------------------------ */
/*  SEO Landing Page Data Registry (Feb 2026)                         */
/*                                                                    */
/*  Powers /shiftbridge/prn-shifts/:slug — generates ~340 unique,     */
/*  Google-indexable URLs targeting long-tail PRN healthcare-staffing */
/*  queries (e.g. "ICU PRN shifts in Houston, TX").                   */
/* ------------------------------------------------------------------ */

/* Top 25 US healthcare-staffing markets by PRN demand volume */
export const TOP_CITIES = [
  { city: "Houston",       state: "TX", slug: "houston-tx",       region: "Gulf Coast",       population: "2.3M" },
  { city: "Dallas",        state: "TX", slug: "dallas-tx",        region: "DFW Metroplex",    population: "1.3M" },
  { city: "Austin",        state: "TX", slug: "austin-tx",        region: "Central Texas",    population: "974K" },
  { city: "San Antonio",   state: "TX", slug: "san-antonio-tx",   region: "South Texas",      population: "1.5M" },
  { city: "Atlanta",       state: "GA", slug: "atlanta-ga",       region: "Southeast",        population: "498K" },
  { city: "Miami",         state: "FL", slug: "miami-fl",         region: "South Florida",    population: "442K" },
  { city: "Orlando",       state: "FL", slug: "orlando-fl",       region: "Central Florida",  population: "316K" },
  { city: "Tampa",         state: "FL", slug: "tampa-fl",         region: "West Florida",     population: "399K" },
  { city: "Phoenix",       state: "AZ", slug: "phoenix-az",       region: "Southwest",        population: "1.6M" },
  { city: "Los Angeles",   state: "CA", slug: "los-angeles-ca",   region: "Southern CA",      population: "3.9M" },
  { city: "San Diego",     state: "CA", slug: "san-diego-ca",     region: "Southern CA",      population: "1.4M" },
  { city: "New York",      state: "NY", slug: "new-york-ny",      region: "Tri-State",        population: "8.3M" },
  { city: "Chicago",       state: "IL", slug: "chicago-il",       region: "Midwest",          population: "2.7M" },
  { city: "Philadelphia",  state: "PA", slug: "philadelphia-pa",  region: "Mid-Atlantic",     population: "1.6M" },
  { city: "Boston",        state: "MA", slug: "boston-ma",        region: "New England",      population: "675K" },
  { city: "Seattle",       state: "WA", slug: "seattle-wa",       region: "Pacific Northwest",population: "737K" },
  { city: "Denver",        state: "CO", slug: "denver-co",        region: "Mountain West",    population: "716K" },
  { city: "Las Vegas",     state: "NV", slug: "las-vegas-nv",     region: "Southwest",        population: "646K" },
  { city: "Charlotte",     state: "NC", slug: "charlotte-nc",     region: "Carolinas",        population: "874K" },
  { city: "Nashville",     state: "TN", slug: "nashville-tn",     region: "Mid-South",        population: "692K" },
  { city: "Memphis",       state: "TN", slug: "memphis-tn",       region: "Mid-South",        population: "633K" },
  { city: "Detroit",       state: "MI", slug: "detroit-mi",       region: "Great Lakes",      population: "639K" },
  { city: "Minneapolis",   state: "MN", slug: "minneapolis-mn",   region: "Great Lakes",      population: "429K" },
  { city: "Washington",    state: "DC", slug: "washington-dc",    region: "Mid-Atlantic",     population: "705K" },
  { city: "Baltimore",     state: "MD", slug: "baltimore-md",     region: "Mid-Atlantic",     population: "585K" },
];

/* High-volume PRN specialty searches */
export const SEO_SPECIALTIES = [
  { slug: "rn",        name: "RN",            full: "Registered Nurse",      apiMatch: ["RN", "Registered Nurse"], summary: "Open RN PRN shifts across med-surg, ICU, ER, telemetry and specialty units." },
  { slug: "icu",       name: "ICU",           full: "Intensive Care Unit",   apiMatch: ["ICU"],                    summary: "Critical-care PRN shifts in adult ICU, MICU, SICU, CVICU and neuro ICU." },
  { slug: "er",        name: "ER",            full: "Emergency Room",        apiMatch: ["ER", "Emergency"],        summary: "Level I–IV emergency department PRN shifts with same-day fill options." },
  { slug: "med-surg",  name: "Med-Surg",      full: "Medical-Surgical",      apiMatch: ["Med-Surg", "Med Surg", "Medical Surgical"], summary: "PRN shifts on general medical-surgical floors across acute care hospitals." },
  { slug: "or",        name: "OR",            full: "Operating Room",        apiMatch: ["OR", "Operating Room"],   summary: "Operating room circulator and scrub PRN shifts at vetted surgical facilities." },
  { slug: "labor-delivery", name: "L&D",      full: "Labor & Delivery",      apiMatch: ["L&D", "Labor", "Labor & Delivery"], summary: "OB PRN shifts including L&D, antepartum, postpartum and high-risk units." },
  { slug: "nicu",      name: "NICU",          full: "Neonatal ICU",          apiMatch: ["NICU"],                   summary: "Level II–IV NICU PRN shifts at children's hospitals and regional units." },
  { slug: "telemetry", name: "Telemetry",     full: "Telemetry / Step-Down", apiMatch: ["Telemetry", "Tele", "Stepdown"], summary: "Cardiac telemetry and step-down PRN shifts with continuous monitoring." },
  { slug: "pacu",      name: "PACU",          full: "Post-Anesthesia Care",  apiMatch: ["PACU", "Post Anesthesia"], summary: "PACU and recovery-room PRN shifts at acute and ambulatory surgery centers." },
  { slug: "lpn",       name: "LPN",           full: "Licensed Practical Nurse", apiMatch: ["LPN"],                 summary: "LPN PRN shifts in long-term care, skilled nursing, and hospital floors." },
  { slug: "cna",       name: "CNA",           full: "Certified Nursing Assistant", apiMatch: ["CNA"],              summary: "CNA and PCT PRN shifts at hospitals, SNFs and assisted-living facilities." },
  { slug: "therapy",   name: "Therapy",       full: "Therapy (PT/OT/SLP/RT)", apiMatch: ["PT", "OT", "SLP", "RT", "Therapy", "Respiratory"], summary: "Therapy PRN shifts: physical, occupational, speech and respiratory disciplines." },
];

/* Slug-parsing helpers ------------------------------------------------ */

/** Determine page type from slug:
 *  - "icu"          → specialty
 *  - "houston-tx"   → location (city-state)
 *  - "icu-houston-tx" → combo (specialty + city-state)
 */
export function parseSeoSlug(slug) {
  if (!slug) return { type: "invalid" };

  /* Try direct specialty match */
  const specialty = SEO_SPECIALTIES.find((s) => s.slug === slug);
  if (specialty) return { type: "specialty", specialty };

  /* Try direct city match */
  const city = TOP_CITIES.find((c) => c.slug === slug);
  if (city) return { type: "location", city };

  /* Try combo: specialty-{citySlug} — iterate to find longest specialty match */
  for (const sp of SEO_SPECIALTIES) {
    const prefix = `${sp.slug}-`;
    if (slug.startsWith(prefix)) {
      const cityPart = slug.slice(prefix.length);
      const cityMatch = TOP_CITIES.find((c) => c.slug === cityPart);
      if (cityMatch) return { type: "combo", specialty: sp, city: cityMatch };
    }
  }

  return { type: "invalid" };
}

/** Build all valid slugs (for sitemap + hub listing). */
export function buildAllSeoSlugs() {
  const slugs = [];
  /* Specialty-only */
  SEO_SPECIALTIES.forEach((s) => slugs.push({ slug: s.slug, type: "specialty", weight: 0.7 }));
  /* City-only */
  TOP_CITIES.forEach((c) => slugs.push({ slug: c.slug, type: "location", weight: 0.7 }));
  /* Combo */
  SEO_SPECIALTIES.forEach((s) =>
    TOP_CITIES.forEach((c) => slugs.push({ slug: `${s.slug}-${c.slug}`, type: "combo", weight: 0.8 }))
  );
  return slugs;
}

/** Filter a shifts list by city+state and/or specialty matches. */
export function filterShifts(shifts, { city = null, specialty = null } = {}) {
  if (!Array.isArray(shifts)) return [];
  return shifts.filter((s) => {
    if (city) {
      const cityOk = (s.city || "").toLowerCase() === city.city.toLowerCase();
      const stateOk = (s.state || "").toUpperCase() === city.state.toUpperCase();
      if (!cityOk && !stateOk) return false;
    }
    if (specialty) {
      const spec = (s.specialty || "").toLowerCase();
      const ok = specialty.apiMatch.some((m) => spec.includes(m.toLowerCase()));
      if (!ok) return false;
    }
    return true;
  });
}

/** Generate page title / description / breadcrumb for SEO based on parsed slug. */
export function buildSeoMeta(parsed) {
  if (parsed.type === "specialty") {
    const s = parsed.specialty;
    return {
      title: `${s.full} PRN Shifts — Open ${s.name} Jobs Nationwide | TRITAL ShiftBridge™`,
      h1: `${s.name} PRN Shifts Across the United States`,
      description: `Pick up ${s.full} PRN shifts at vetted hospitals & SNFs nationwide. W-2 weekly pay, transparent rates, zero clinician fees. ${s.summary} Powered by TRITAL Care®.`,
      keywords: `${s.name} PRN, ${s.name} per diem, ${s.name} PRN jobs, ${s.full} PRN shifts, ${s.name} nurse PRN, ${s.full} per diem nursing, ${s.name} W-2 PRN`,
      crumb: `${s.name} PRN Shifts`,
    };
  }
  if (parsed.type === "location") {
    const c = parsed.city;
    return {
      title: `PRN Shifts in ${c.city}, ${c.state} — Local Per-Diem Healthcare Jobs | TRITAL ShiftBridge™`,
      h1: `PRN Shifts in ${c.city}, ${c.state}`,
      description: `Open PRN nursing, allied health and therapy shifts in ${c.city}, ${c.state}. ${c.region} hospitals and SNFs hiring now. W-2 weekly pay, transparent rates, zero clinician fees. Powered by TRITAL Care®.`,
      keywords: `PRN shifts ${c.city} ${c.state}, per diem nursing ${c.city}, ${c.city} ${c.state} PRN, ${c.city} per diem jobs, PRN jobs near me ${c.city}, ${c.city} nurse PRN, ${c.region} PRN healthcare`,
      crumb: `PRN Shifts in ${c.city}, ${c.state}`,
    };
  }
  if (parsed.type === "combo") {
    const { specialty: s, city: c } = parsed;
    return {
      title: `${s.full} PRN Shifts in ${c.city}, ${c.state} — Open ${s.name} Jobs | TRITAL ShiftBridge™`,
      h1: `${s.name} PRN Shifts in ${c.city}, ${c.state}`,
      description: `${s.full} PRN shifts in ${c.city}, ${c.state}. Pick up vetted ${s.name} shifts at ${c.region} hospitals and SNFs. W-2 weekly pay, transparent rates, zero clinician fees. ${s.summary}`,
      keywords: `${s.name} PRN ${c.city}, ${s.name} PRN ${c.state}, ${s.full} PRN ${c.city} ${c.state}, ${s.name} per diem ${c.city}, ${s.name} nurse jobs ${c.city}, ${c.city} ${s.name} PRN, ${s.name} PRN near me ${c.city}`,
      crumb: `${s.name} PRN Shifts in ${c.city}, ${c.state}`,
    };
  }
  return null;
}
